// pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthModal from '../components/AuthModal';
import { getServices } from '../services/api';
import { useCart } from '../context/CartContext';
import { 
  Search, 
  Shield, 
  Clock, 
  Award, 
  Users, 
  CheckCircle, 
  ArrowRight, 
  Sparkles,
  Star,
  ChevronRight,
  MapPin,
  Calendar,
  ThumbsUp,
  Home,
  Droplets,
  Zap,
  Wrench,
  Flame,
  Brush,
  Wind,
  Snowflake,
  Car,
  Phone,
  MessageCircle,
  Menu,
  X,
  ArrowLeft,
  ShoppingCart,
  Loader,
  TrendingUp,
  Heart
} from 'lucide-react';

const HomePage = ({ user, onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authType, setAuthType] = useState('login');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Quick Add to Cart Modal States
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddService, setQuickAddService] = useState(null);
  const [quickAddBHK, setQuickAddBHK] = useState('1BHK');
  const [quickAddVariant, setQuickAddVariant] = useState(null);
  const [quickAddQuantity, setQuickAddQuantity] = useState(1);
  
  // Primary color scheme
  const primaryColor = '#096651';
  const primaryLight = '#0d8a72';
  const primaryDark = '#064a3d';
  const accentColor = '#ff7e5f';
  const lightBg = '#f8fffd';
  const textDark = '#1a2b23';

  // Categories like Urban Company
  const categories = [
    { id: 'AcService', name: 'AC Services', icon: Snowflake, color: '#00A8E8' },
    { id: 'Handyman', name: 'Handyman Services', icon: Wrench, color: '#2196F3' },
    { id: 'Cleaning', name: 'Cleaning Services', icon: Brush, color: '#FF9800' },
    { id: 'kitchenappliance', name: 'Kitchen Appliances', icon: Flame, color: '#9C27B0' },
    { id: 'TVandEntertainment', name: 'TV & Entertainment', icon: Sparkles, color: '#00BCD4' },
    { id: 'Homeappliance', name: 'Home Appliance Service', icon: Home, color: '#FF5722' },
    { id: 'beauty', name: 'Beauty & Personal Care', icon: Heart, color: '#795548' },
  ];

  // Logo image path
  const logoImage = '/logo.png';

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesData = await getServices('', '', '1BHK', false);
        setServices(servicesData);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Handle redirect from booking page with auth prompt
  useEffect(() => {
    if (location.state?.showAuth && !user) {
      setAuthType('login');
      setShowAuthModal(true);
      
      if (location.state?.message) {
        setTimeout(() => {
          alert(location.state.message);
        }, 100);
      }
      
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, user, navigate]);

  const handleServiceClick = (service) => {
    if (!user) {
      setAuthType('login');
      setShowAuthModal(true);
    } else {
      navigate('/booking', { state: { service } });
    }
  };

  // Handle category click to navigate to services page
  const handleCategoryClick = (category) => {
    const categoryMap = {
      'cleaning': 'Home Cleaning',
      'plumbing': 'Plumbing Services',
      'electrical': 'Electrical Services',
      'repair': 'Repair Services',
      'ac': 'AC Services',
      'painting': 'Painting Services',
      'carpentry': 'Carpentry Services',
      'appliance': 'Appliance Repair',
      'car': 'Car Services'
    };
    
    const categoryName = categoryMap[category.id] || category.name;
    navigate('/services', { state: { selectedCategory: categoryName } });
  };

  // Enhanced search handler with navigation to services page
  const handleSearch = () => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      navigate('/services', { state: { searchQuery: trimmedQuery } });
    } else {
      navigate('/services');
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Filter services by category
  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => {
        const categoryMap = {
          'cleaning': 'Cleaning Services',
          'plumbing': 'Plumber Service',
          'electrical': 'Electrician Service',
          'ac': 'AC Service',
          'painting': 'Painting Services',
          'carpentry': 'Carpenter Service',
          'appliance': 'Appliance Repair'
        };
        
        const mappedCategory = categoryMap[selectedCategory];
        return mappedCategory ? service.category === mappedCategory : false;
      });

  // Popular services - Check for both 'popular' and 'isPopular' fields
  const popularServices = services
    .filter(service => service.popular === true || service.isPopular === true)
    .slice(0, isMobile ? 6 : 8);

  // Featured services (simulated)
  const featuredServices = services.slice(0, isMobile ? 3 : 4);

  // Handle quick add to cart
  const handleQuickAddToCart = (service) => {
    if (!user) {
      setAuthType('login');
      setShowAuthModal(true);
      return;
    }

    setQuickAddService(service);
    setQuickAddBHK('1BHK');
    setQuickAddQuantity(1);
    
    if (service.variants && service.variants.length > 0) {
      setQuickAddVariant(service.variants[0]);
    } else {
      setQuickAddVariant(null);
    }
    
    setShowQuickAddModal(true);
  };

  // Confirm quick add to cart
  const confirmQuickAdd = () => {
    if (!quickAddService) return;

    const serviceToAdd = { ...quickAddService };
    
    if (quickAddService.pricingType === 'bhk-based') {
      serviceToAdd.selectedBHK = quickAddBHK;
      serviceToAdd.price = quickAddService.bhkPrices[quickAddBHK];
    } else if (quickAddService.pricingType === 'variants' && quickAddVariant) {
      serviceToAdd.selectedVariant = quickAddVariant;
      serviceToAdd.price = quickAddVariant.price;
      serviceToAdd.name = `${quickAddService.name} - ${quickAddVariant.name}`;
    } else {
      serviceToAdd.price = quickAddService.calculatedPrice || quickAddService.basePrice || quickAddService.price;
    }

    addToCart(serviceToAdd, quickAddQuantity);
    setShowQuickAddModal(false);
    setQuickAddService(null);
    
    // Show success message
    if (!isMobile) {
      alert(`${quickAddService.name} added to cart!`);
    }
  };

  // Parse service images
  const getServiceImages = (service) => {
    if (!service) return [];
    
    const imageData = service.images || service.image;
    
    if (!imageData) return [];
    
    if (typeof imageData === 'string') {
      try {
        return JSON.parse(imageData);
      } catch (e) {
        return [imageData];
      }
    }
    
    if (Array.isArray(imageData)) {
      return imageData;
    }
    
    return [];
  };

  // Get service icon
  const getServiceIcon = (serviceName) => {
    const iconProps = { size: 48 };
    
    const icons = {
      'House Cleaning': <Home {...iconProps} />,
      'Kitchen Cleaning': <Home {...iconProps} />,
      'Bathroom Cleaning': <Home {...iconProps} />,
      'AC Service': <Wind {...iconProps} />,
      'AC Repair': <Wind {...iconProps} />,
      'Refrigerator Repair': <Home {...iconProps} />,
      'Washing Machine Repair': <Home {...iconProps} />,
      'Electrician Service': <Zap {...iconProps} />,
      'Plumber Service': <Droplets {...iconProps} />,
      'Carpenter Service': <Wrench {...iconProps} />,
    };
    
    return icons[serviceName] || <Home {...iconProps} />;
  };

  // Service Card Component for Homepage
  const HomeServiceCard = ({ service, onBookService, onAddToCart, primaryColor, accentColor, featured, isMobile, showPopularBadge = false }) => {
    const images = getServiceImages(service);
    const primaryImage = images.length > 0 ? images[0] : null;
    
    const cardStyle = {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: featured ? `0 10px 30px ${primaryColor}20` : '0 5px 20px rgba(0, 0, 0, 0.05)',
      border: `1px solid ${primaryColor}10`,
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      animation: 'fadeIn 0.5s ease-out',
      position: 'relative',
    };
    
    const imageStyle = {
      width: '100%',
      height: isMobile ? '180px' : '200px',
      objectFit: 'cover',
      backgroundColor: '#f5f5f5',
    };
    
    const iconWrapperStyle = {
      width: '100%',
      height: isMobile ? '180px' : '200px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      color: primaryColor,
    };

    return (
      <div 
        className="service-card"
        style={cardStyle}
        onClick={() => onBookService(service)}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = featured ? `0 10px 30px ${primaryColor}20` : '0 5px 20px rgba(0, 0, 0, 0.05)';
          }
        }}
        onTouchStart={(e) => {
          if (isMobile) {
            e.currentTarget.style.transform = 'scale(0.99)';
          }
        }}
        onTouchEnd={(e) => {
          if (isMobile) {
            e.currentTarget.style.transform = 'scale(1)';
          }
        }}
      >
        
        
        {primaryImage ? (
          <img 
            src={primaryImage} 
            alt={service.name}
            style={imageStyle}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : (
          <div style={iconWrapperStyle}>
            {getServiceIcon(service.name)}
          </div>
        )}
        
        <div style={{ padding: isMobile ? '15px' : '20px' }}>
          <h3 style={{
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: '600',
            marginBottom: '8px',
            color: textDark,
            lineHeight: '1.3',
          }}>
            {service.name}
          </h3>
          
          <p style={{
            fontSize: isMobile ? '12px' : '14px',
            color: '#666',
            marginBottom: '15px',
            lineHeight: '1.5',
            minHeight: isMobile ? '40px' : '60px',
          }}>
            {service.description && service.description.length > (isMobile ? 60 : 80) 
              ? `${service.description.substring(0, isMobile ? 60 : 80)}...` 
              : service.description || 'Professional service by verified experts'}
          </p>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '15px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}>
              <Star size={14} color="#FFD700" fill="#FFD700" />
              <span style={{
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                color: textDark,
              }}>
                {service.rating || '4.5'}
              </span>
              <span style={{
                fontSize: isMobile ? '11px' : '12px',
                color: '#666',
              }}>
                ({service.reviews || '100'}+)
              </span>
            </div>
            
            <div style={{
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: '700',
              color: primaryColor,
            }}>
              {service.pricingType === 'inspection' ? (
                <span>₹{service.visitingCharge || service.basePrice}</span>
              ) : service.pricingType === 'bhk-based' && service.bhkPrices ? (
                <span>From ₹{service.bhkPrices['1BHK'] || service.basePrice || 0}</span>
              ) : service.pricingType === 'variants' && service.variants && service.variants.length > 0 ? (
                <span>From ₹{Math.min(...service.variants.map(v => v.price))}</span>
              ) : service.pricingType === 'fixed' ? (
                <span>₹{service.basePrice || 0}</span>
              ) : (
                <span>₹{service.calculatedPrice || service.basePrice || 0}</span>
              )}
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            gap: '10px',
          }}>
            <button
              style={{
                flex: 1,
                backgroundColor: '#f0f0f0',
                color: textDark,
                border: 'none',
                padding: isMobile ? '10px' : '12px',
                borderRadius: '8px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart(service);
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = '#e0e0e0';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = '#f0f0f0';
                }
              }}
              onTouchStart={(e) => {
                if (isMobile) {
                  e.target.style.backgroundColor = '#e0e0e0';
                }
              }}
              onTouchEnd={(e) => {
                if (isMobile) {
                  e.target.style.backgroundColor = '#f0f0f0';
                }
              }}
            >
              <ShoppingCart size={isMobile ? 14 : 16} />
              Add
            </button>
            
            <button
              style={{
                flex: 2,
                backgroundColor: primaryColor,
                color: 'white',
                border: 'none',
                padding: isMobile ? '10px' : '12px',
                borderRadius: '8px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onClick={(e) => {
                e.stopPropagation();
                onBookService(service);
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = primaryLight;
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = primaryColor;
                  e.target.style.transform = 'translateY(0)';
                }
              }}
              onTouchStart={(e) => {
                if (isMobile) {
                  e.target.style.backgroundColor = primaryLight;
                }
              }}
              onTouchEnd={(e) => {
                if (isMobile) {
                  e.target.style.backgroundColor = primaryColor;
                }
              }}
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Inline styles - Add Popular Services section style
  const styles = {
    page: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      color: textDark,
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      overflowX: 'hidden',
    },
    // Hero Section
    hero: {
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
      color: 'white',
      padding: isMobile ? '60px 0 30px' : '80px 0 40px',
      position: 'relative',
      overflow: 'hidden',
    },
    heroContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 16px' : '0 20px',
      position: 'relative',
      zIndex: 2,
    },
    heroContent: {
      maxWidth: '600px',
      marginBottom: isMobile ? '40px' : '60px',
    },
    heroBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '500',
      marginBottom: '20px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    heroTitle: {
      fontSize: isMobile ? 'clamp(1.8rem, 5vw, 2.2rem)' : 'clamp(2.5rem, 5vw, 3.5rem)',
      fontWeight: '800',
      lineHeight: '1.2',
      marginBottom: isMobile ? '15px' : '20px',
    },
    heroSubtitle: {
      fontSize: isMobile ? 'clamp(0.9rem, 2vw, 1rem)' : 'clamp(1rem, 2vw, 1.25rem)',
      opacity: '0.9',
      marginBottom: isMobile ? '30px' : '40px',
      lineHeight: '1.6',
    },
    heroSearch: {
      backgroundColor: 'white',
      borderRadius: isMobile ? '8px' : '12px',
      padding: isMobile ? '3px' : '4px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      maxWidth: '600px',
      boxShadow: '0 20px 60px rgba(9, 102, 81, 0.3)',
      gap: isMobile ? '0' : '0',
    },
    searchInput: {
      flex: 1,
      border: 'none',
      outline: 'none',
      padding: isMobile ? '16px' : '20px',
      fontSize: isMobile ? '14px' : '16px',
      borderRadius: isMobile ? '6px 6px 0 0' : '8px 0 0 8px',
      width: '100%',
    },
    searchButton: {
      backgroundColor: accentColor,
      color: 'white',
      border: 'none',
      padding: isMobile ? '16px 20px' : '20px 40px',
      borderRadius: isMobile ? '0 0 6px 6px' : '0 8px 8px 0',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      width: isMobile ? '100%' : 'auto',
    },
    heroStats: {
      display: 'flex',
      gap: isMobile ? '20px' : '40px',
      marginTop: '40px',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'center' : 'flex-start',
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    statNumber: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '700',
      color: '#ffd166',
    },
    statLabel: {
      fontSize: isMobile ? '12px' : '14px',
      opacity: '0.9',
    },
    // Categories Section
    categories: {
      padding: isMobile ? '40px 0' : '60px 0',
      backgroundColor: lightBg,
    },
    sectionContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 16px' : '0 20px',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '30px',
      flexWrap: 'wrap',
      gap: '20px',
    },
    sectionTitle: {
      fontSize: isMobile ? 'clamp(1.4rem, 3vw, 1.8rem)' : 'clamp(1.8rem, 3vw, 2.5rem)',
      fontWeight: '700',
      color: textDark,
    },
    viewAllLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: primaryColor,
      textDecoration: 'none',
      fontWeight: '600',
      fontSize: isMobile ? '14px' : '16px',
    },
    categoriesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(100px, 1fr))' : 'repeat(auto-fill, minmax(120px, 1fr))',
      gap: isMobile ? '12px' : '20px',
      marginBottom: '40px',
    },
    categoryCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: isMobile ? '15px 10px' : '20px 15px',
      backgroundColor: 'white',
      borderRadius: isMobile ? '10px' : '12px',
      border: `1px solid ${primaryColor}10`,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textAlign: 'center',
      minHeight: isMobile ? '110px' : '120px',
      justifyContent: 'center',
    },
    categoryIcon: {
      width: isMobile ? '40px' : '50px',
      height: isMobile ? '40px' : '50px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: isMobile ? '8px' : '12px',
    },
    categoryName: {
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      color: textDark,
    },
    // Services Section
    services: {
      padding: isMobile ? '40px 0' : '60px 0',
    },
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: isMobile ? '20px' : '30px',
      marginBottom: '40px',
    },
    // Popular Services Section - ADDED
    popularServices: {
      padding: isMobile ? '40px 0' : '60px 0',
      backgroundColor: '#fff9f5',
      background: `linear-gradient(180deg, ${lightBg} 0%, #fff9f5 100%)`,
      position: 'relative',
      overflow: 'hidden',
    },
    popularServicesHeader: {
      textAlign: 'center',
      marginBottom: isMobile ? '30px' : '40px',
    },
    popularServicesTitle: {
      fontSize: isMobile ? 'clamp(1.5rem, 3vw, 2rem)' : 'clamp(2rem, 3vw, 2.8rem)',
      fontWeight: '700',
      color: textDark,
      marginBottom: '10px',
      position: 'relative',
      display: 'inline-block',
    },
    popularServicesSubtitle: {
      fontSize: isMobile ? '14px' : '16px',
      color: '#666',
      maxWidth: '600px',
      margin: '0 auto 20px',
      lineHeight: '1.6',
    },
    popularServicesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: isMobile ? '20px' : '30px',
      marginBottom: '40px',
    },
    // How It Works
    howItWorks: {
      padding: isMobile ? '40px 0' : '80px 0',
      backgroundColor: 'white',
    },
    stepsContainer: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: isMobile ? '30px' : '40px',
      maxWidth: '900px',
      margin: '0 auto',
    },
    step: {
      textAlign: 'center',
      position: 'relative',
    },
    stepIcon: {
      width: isMobile ? '60px' : '80px',
      height: isMobile ? '60px' : '80px',
      backgroundColor: `${primaryColor}10`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      position: 'relative',
    },
    stepNumber: {
      position: 'absolute',
      top: '-10px',
      right: '-10px',
      width: isMobile ? '25px' : '30px',
      height: isMobile ? '25px' : '30px',
      backgroundColor: primaryColor,
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
    },
    stepTitle: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: '600',
      marginBottom: '10px',
      color: textDark,
    },
    stepDescription: {
      fontSize: isMobile ? '13px' : '14px',
      color: '#666',
      lineHeight: '1.5',
    },
    // Why Choose Us
    whyChooseUs: {
      padding: isMobile ? '40px 0' : '80px 0',
      backgroundColor: lightBg,
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: isMobile ? '20px' : '30px',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    feature: {
      backgroundColor: 'white',
      padding: isMobile ? '20px' : '30px',
      borderRadius: '16px',
      textAlign: 'center',
      border: `1px solid ${primaryColor}10`,
      transition: 'all 0.3s ease',
    },
    featureIcon: {
      width: isMobile ? '50px' : '60px',
      height: isMobile ? '50px' : '60px',
      backgroundColor: `${primaryColor}10`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      color: primaryColor,
    },
    featureTitle: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '600',
      marginBottom: '10px',
      color: textDark,
    },
    featureDescription: {
      fontSize: isMobile ? '13px' : '14px',
      color: '#666',
      lineHeight: '1.5',
    },
    // CTA Section
    cta: {
      padding: isMobile ? '50px 0' : '80px 0',
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
      color: 'white',
      textAlign: 'center',
    },
    ctaTitle: {
      fontSize: isMobile ? 'clamp(1.5rem, 4vw, 2rem)' : 'clamp(2rem, 4vw, 3rem)',
      fontWeight: '700',
      marginBottom: '20px',
    },
    ctaDescription: {
      fontSize: isMobile ? 'clamp(0.9rem, 2vw, 1rem)' : 'clamp(1rem, 2vw, 1.25rem)',
      opacity: '0.9',
      maxWidth: '600px',
      margin: '0 auto 30px',
      padding: isMobile ? '0 16px' : '0',
    },
    ctaButton: {
      backgroundColor: 'white',
      color: primaryColor,
      border: 'none',
      padding: isMobile ? '16px 30px' : '18px 50px',
      borderRadius: isMobile ? '10px' : '12px',
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
    },
    // Footer
    footer: {
      backgroundColor: '#1a2b23',
      color: 'white',
      padding: isMobile ? '40px 0 20px' : '60px 0 30px',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 16px' : '0 20px',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: isMobile ? '30px' : '40px',
      marginBottom: '40px',
    },
    footerSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
    },
    footerTitle: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '600',
      marginBottom: '10px',
      color: 'white',
    },
    footerLink: {
      color: '#ccc',
      textDecoration: 'none',
      fontSize: isMobile ? '13px' : '14px',
      transition: 'color 0.3s ease',
    },
    footerBottom: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '15px' : '20px',
      textAlign: 'center',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      color: '#ccc',
      fontSize: isMobile ? '12px' : '14px',
    },
    // Loading
    loading: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '300px',
      gap: '20px',
    },
    loadingLogo: {
      width: isMobile ? '80px' : '120px',
      height: isMobile ? '80px' : '120px',
      objectFit: 'contain',
      animation: 'pulse 1.5s ease-in-out infinite',
    },
    loadingSpinner: {
      border: `4px solid ${lightBg}`,
      borderTop: `4px solid ${primaryColor}`,
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      animation: 'spin 1s linear infinite',
    },
    // Mobile Menu
    mobileMenu: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: isMobile ? '100%' : '300px',
      backgroundColor: 'white',
      zIndex: 1000,
      padding: isMobile ? '60px 20px 20px' : '40px 20px',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease',
      boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.1)',
    },
    // Navigation
    navigation: {
      position: 'absolute',
      top: isMobile ? '20px' : '40px',
      left: isMobile ? '16px' : '20px',
      right: isMobile ? '16px' : '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 10,
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: isMobile ? '18px' : '24px',
      fontWeight: '700',
      color: 'white',
      textDecoration: 'none',
    },
    logoImage: {
      width: isMobile ? '30px' : '40px',
      height: isMobile ? '30px' : '40px',
      objectFit: 'contain',
    },
    navLinks: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      gap: '30px',
    },
    navLink: {
      color: 'white',
      textDecoration: 'none',
      fontSize: '16px',
      fontWeight: '500',
      transition: 'all 0.3s ease',
    },
    authButtons: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    loginBtn: {
      backgroundColor: 'transparent',
      color: 'white',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      padding: '10px 25px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    signupBtn: {
      backgroundColor: 'white',
      color: primaryColor,
      border: 'none',
      padding: '10px 25px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    mobileMenuBtn: {
      display: isMobile ? 'block' : 'none',
      background: 'none',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '6px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    // Quick Add Modal
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1001,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '16px' : '20px',
      animation: 'fadeIn 0.3s ease',
    },
    quickAddModal: {
      backgroundColor: 'white',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '20px' : '30px',
      maxWidth: '500px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      animation: 'slideUp 0.3s ease',
    },
    modalClose: {
      position: 'absolute',
      top: '15px',
      right: '15px',
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#666',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
    },
    modalSection: {
      marginBottom: '20px',
    },
    bhkOptions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
      gap: '10px',
      marginTop: '10px',
    },
    bhkBtn: {
      padding: '10px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'none',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '5px',
      transition: 'all 0.3s ease',
    },
    variantOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      marginTop: '10px',
    },
    variantBtn: {
      padding: '12px 15px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'none',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'all 0.3s ease',
    },
    quantityControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      marginTop: '10px',
    },
    quantityBtn: {
      width: '40px',
      height: '40px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'none',
      cursor: 'pointer',
      fontSize: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    modalActions: {
      display: 'flex',
      gap: '15px',
      marginTop: '30px',
    },
    btnCancel: {
      flex: 1,
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
    },
    btnAddToCart: {
      flex: 2,
      padding: '12px',
      border: 'none',
      borderRadius: '8px',
      background: primaryColor,
      color: 'white',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
  };

  return (
    <div style={styles.page}>
      {/* Navigation */}
      <div style={styles.navigation}>
        <div style={styles.logo}>
          <img 
            src={logoImage} 
            alt="EaseMyHome Logo" 
            style={styles.logoImage}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <Shield size={isMobile ? 24 : 32} color="white" style={{ display: 'none' }} />
          <span>EaseMyHome</span>
        </div>
        
        <div style={styles.navLinks}>
          <a href="#services" style={styles.navLink}>Services</a>
          <a href="#how-it-works" style={styles.navLink}>How it Works</a>
          <a href="#why-choose-us" style={styles.navLink}>Why Choose Us</a>
        </div>
        
        {!user && (
          <div style={styles.authButtons}>
            <button 
              style={styles.loginBtn}
              onClick={() => {
                setAuthType('login');
                setShowAuthModal(true);
              }}
            >
              Login
            </button>
            <button 
              style={styles.signupBtn}
              onClick={() => {
                setAuthType('signup');
                setShowAuthModal(true);
              }}
            >
              Sign Up
            </button>
          </div>
        )}
        
        <button 
          style={styles.mobileMenuBtn}
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContainer}>
          <div style={styles.heroContent}>
            
            
            <h1 style={styles.heroTitle}>
              Professional Home Services
              <span style={{ display: 'block', color: '#ffd166' }}>At Your Doorstep</span>
            </h1>
            
            <p style={styles.heroSubtitle}>
              Book verified professionals for cleaning, repairs, maintenance, and more. 
              Quality service guaranteed.
            </p>
            
            <div style={styles.heroSearch}>
              <input
                type="text"
                placeholder="What service do you need today? (e.g., AC repair, home cleaning, plumber...)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                style={styles.searchInput}
              />
              <button 
                style={styles.searchButton}
                onClick={handleSearch}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.target.style.backgroundColor = '#ff6b4a';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.target.style.backgroundColor = accentColor;
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
                onTouchStart={(e) => {
                  if (isMobile) {
                    e.target.style.backgroundColor = '#ff6b4a';
                  }
                }}
                onTouchEnd={(e) => {
                  if (isMobile) {
                    e.target.style.backgroundColor = accentColor;
                  }
                }}
              >
                <Search size={isMobile ? 18 : 20} />
                Find Services
              </button>
            </div>
            
           
          </div>
        </div>
        
        {/* Background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          zIndex: 1,
        }}></div>
      </section>

      {/* Loading State */}
      {loading && (
        <div style={styles.loading}>
          <img 
            src={logoImage} 
            alt="Loading" 
            style={styles.loadingLogo}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'block';
            }}
          />
          <div style={styles.loadingSpinner}></div>
          <p style={{ color: primaryColor, fontWeight: '600' }}>Loading services...</p>
        </div>
      )}

      {/* Categories Section */}
      {!loading && (
        <section style={styles.categories}>
          <div style={styles.sectionContainer}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Browse by Category</h2>
            </div>
            
            <div style={styles.categoriesGrid}>
              {categories.map((category) => (
                <div 
                  key={category.id}
                  style={{
                    ...styles.categoryCard,
                    border: selectedCategory === category.id 
                      ? `2px solid ${primaryColor}` 
                      : `1px solid ${primaryColor}10`,
                    transform: selectedCategory === category.id ? 'translateY(-5px)' : 'none',
                    boxShadow: selectedCategory === category.id 
                      ? `0 10px 30px ${primaryColor}20` 
                      : 'none',
                  }}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    handleCategoryClick(category);
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile && selectedCategory !== category.id) {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = `0 5px 20px ${primaryColor}10`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile && selectedCategory !== category.id) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                  onTouchStart={(e) => {
                    if (isMobile) {
                      e.currentTarget.style.backgroundColor = `${primaryColor}05`;
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (isMobile) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  <div style={{
                    ...styles.categoryIcon,
                    backgroundColor: `${category.color}15`,
                    color: category.color,
                  }}>
                    <category.icon size={isMobile ? 20 : 24} />
                  </div>
                  <div style={styles.categoryName}>{category.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      {!loading && (
        <section id="services" style={styles.services}>
          <div style={styles.sectionContainer}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>
                {selectedCategory === 'all' ? 'All Services' : 
                 categories.find(c => c.id === selectedCategory)?.name + ' Services'}
              </h2>
              <button 
                style={{
                  ...styles.signupBtn,
                  padding: isMobile ? '10px 20px' : '12px 30px',
                  fontSize: isMobile ? '13px' : '14px',
                }}
                onClick={() => navigate('/services')}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.target.style.backgroundColor = primaryLight;
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.target.style.backgroundColor = primaryColor;
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
                onTouchStart={(e) => {
                  if (isMobile) {
                    e.target.style.backgroundColor = primaryLight;
                  }
                }}
                onTouchEnd={(e) => {
                  if (isMobile) {
                    e.target.style.backgroundColor = primaryColor;
                  }
                }}
              >
                View All Services
                <ChevronRight size={isMobile ? 14 : 16} />
              </button>
            </div>
            
            <div style={styles.servicesGrid}>
              {filteredServices.slice(0, isMobile ? 4 : 6).map((service, index) => (
                <HomeServiceCard
                  key={service.id || index}
                  service={service}
                  onBookService={handleServiceClick}
                  onAddToCart={handleQuickAddToCart}
                  primaryColor={primaryColor}
                  accentColor={accentColor}
                  isMobile={isMobile}
                />
              ))}
            </div>
            
            {filteredServices.length > (isMobile ? 4 : 6) && (
              <div style={{ textAlign: 'center', marginTop: '40px' }}>
                <button 
                  style={{
                    ...styles.viewAllLink,
                    border: `2px solid ${primaryColor}`,
                    padding: isMobile ? '10px 20px' : '12px 30px',
                    borderRadius: '8px',
                    background: 'none',
                    cursor: 'pointer',
                    margin: '0 auto',
                    display: 'inline-flex',
                  }}
                  onClick={() => navigate('/services')}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = `${primaryColor}05`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  onTouchStart={(e) => {
                    if (isMobile) {
                      e.target.style.backgroundColor = `${primaryColor}05`;
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (isMobile) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  View More Services
                  <ChevronRight size={isMobile ? 14 : 16} />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ========== POPULAR SERVICES SECTION ========== */}
      {!loading && popularServices.length > 0 && (
        <section style={styles.popularServices} id="popular-services">
          <div style={styles.sectionContainer}>
            <div style={styles.popularServicesHeader}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '15px',
              }}>
                
                <h2 style={styles.popularServicesTitle}>
                  Most Popular Services
                </h2>
              </div>
              
              <p style={styles.popularServicesSubtitle}>
                Our most loved services by thousands of customers. These services have the highest ratings and customer satisfaction.
              </p>
              
              
                
                
                  
                
            </div>
            
            <div style={styles.popularServicesGrid}>
              {popularServices.map((service, index) => (
                <HomeServiceCard
                  key={`popular-${service.id || index}`}
                  service={service}
                  onBookService={handleServiceClick}
                  onAddToCart={handleQuickAddToCart}
                  primaryColor={primaryColor}
                  accentColor={accentColor}
                  featured={true}
                  isMobile={isMobile}
                  showPopularBadge={true}
                />
              ))}
            </div>
            
            {popularServices.length > (isMobile ? 4 : 6) && (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <button 
                  style={{
                    ...styles.viewAllLink,
                    border: `2px solid ${accentColor}`,
                    padding: isMobile ? '10px 20px' : '12px 30px',
                    borderRadius: '8px',
                    background: 'none',
                    cursor: 'pointer',
                    margin: '0 auto',
                    display: 'inline-flex',
                    color: accentColor,
                  }}
                  onClick={() => navigate('/services', { state: { showPopular: true } })}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = `${accentColor}05`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  onTouchStart={(e) => {
                    if (isMobile) {
                      e.target.style.backgroundColor = `${accentColor}05`;
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (isMobile) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  View All Popular Services
                  <ChevronRight size={isMobile ? 14 : 16} />
                </button>
              </div>
            )}
            
            {/* Statistics */}
           
          </div>
          
          {/* Background decoration */}
          <div style={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: '200px',
            height: '200px',
            background: `radial-gradient(circle, ${accentColor}20 0%, transparent 70%)`,
            borderRadius: '50%',
            zIndex: 0,
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '10%',
            left: '5%',
            width: '150px',
            height: '150px',
            background: `radial-gradient(circle, ${primaryColor}15 0%, transparent 70%)`,
            borderRadius: '50%',
            zIndex: 0,
          }}></div>
        </section>
      )}

      {/* How It Works */}
      {!loading && (
        <section id="how-it-works" style={styles.howItWorks}>
          <div style={styles.sectionContainer}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '60px' }}>
              <h2 style={{ ...styles.sectionTitle, marginBottom: '15px' }}>
                How It Works
              </h2>
              <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#666', maxWidth: '600px', margin: '0 auto' }}>
                Book a professional service in just 3 easy steps
              </p>
            </div>
            
            <div style={styles.stepsContainer}>
              {[
                {
                  icon: Search,
                  title: 'Browse & Select',
                  description: 'Choose from 50+ home services and select the one you need',
                  number: 1,
                },
                {
                  icon: Calendar,
                  title: 'Schedule Appointment',
                  description: 'Pick your preferred date and time slot for the service',
                  number: 2,
                },
                {
                  icon: Users,
                  title: 'Get Service Done',
                  description: 'Our verified expert arrives and completes the job professionally',
                  number: 3,
                },
              ].map((step) => (
                <div key={step.number} style={styles.step}>
                  <div style={styles.stepIcon}>
                    <div style={styles.stepNumber}>{step.number}</div>
                    <step.icon size={isMobile ? 24 : 32} color={primaryColor} />
                  </div>
                  <h3 style={styles.stepTitle}>{step.title}</h3>
                  <p style={styles.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      {!loading && (
        <section id="why-choose-us" style={styles.whyChooseUs}>
          <div style={styles.sectionContainer}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '60px' }}>
              <h2 style={{ ...styles.sectionTitle, marginBottom: '15px' }}>
                Why Choose Us
              </h2>
              <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#666', maxWidth: '600px', margin: '0 auto' }}>
                We provide the best home service experience
              </p>
            </div>
            
            <div style={styles.featuresGrid}>
              {[
                {
                  icon: Shield,
                  title: 'Verified Professionals',
                  description: 'All our service providers are background verified and trained',
                },
                {
                  icon: Award,
                  title: 'Quality Guaranteed',
                  description: 'We ensure high-quality service with satisfaction guarantee',
                },
                {
                  icon: Clock,
                  title: 'On-Time Service',
                  description: 'Our professionals arrive on time as per your schedule',
                },
              ].map((feature, index) => (
                <div 
                  key={index} 
                  style={styles.feature}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(-10px)';
                      e.currentTarget.style.boxShadow = `0 20px 40px ${primaryColor}10`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div style={styles.featureIcon}>
                    <feature.icon size={isMobile ? 24 : 32} />
                  </div>
                  <h3 style={styles.featureTitle}>{feature.title}</h3>
                  <p style={styles.featureDescription}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {!loading && (
        <section style={styles.cta}>
          <div style={styles.sectionContainer}>
            <h2 style={styles.ctaTitle}>Ready to Book a Service?</h2>
            <p style={styles.ctaDescription}>
              Join thousands of satisfied customers who trust us with their home service needs
            </p>
            <button 
              style={styles.ctaButton}
              onClick={() => navigate('/services')}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = '#f0f0f0';
                  e.target.style.transform = 'translateY(-5px)';
                  e.target.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
              onTouchStart={(e) => {
                if (isMobile) {
                  e.target.style.backgroundColor = '#f0f0f0';
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onTouchEnd={(e) => {
                if (isMobile) {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              Book a Service Now
              <ArrowRight size={isMobile ? 18 : 20} />
            </button>
          </div>
        </section>
      )}

      {/* Footer */}
      {!loading && (
        <footer style={styles.footer}>
          <div style={styles.footerContent}>
            <div style={styles.footerSection}>
              <div style={styles.logo}>
                <img 
                  src={logoImage} 
                  alt="EaseMyHome Logo" 
                  style={{ ...styles.logoImage, width: '30px', height: '30px' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <Shield size={24} color="white" style={{ display: 'none' }} />
                <span>EaseMyHome</span>
              </div>
              <p style={{ color: '#ccc', fontSize: isMobile ? '13px' : '14px', lineHeight: '1.6' }}>
                Professional home services at your doorstep. Quality guaranteed.
              </p>
              <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                <Phone size={isMobile ? 16 : 18} color="#ccc" />
                <MessageCircle size={isMobile ? 16 : 18} color="#ccc" />
                <Shield size={isMobile ? 16 : 18} color="#ccc" />
              </div>
            </div>
            
            <div style={styles.footerSection}>
              <h3 style={styles.footerTitle}>Services</h3>
              <a href="/services" style={styles.footerLink} onClick={(e) => {
                e.preventDefault();
                navigate('/services', { state: { selectedCategory: 'Home Cleaning' } });
              }}>Home Cleaning</a>
              <a href="/services" style={styles.footerLink} onClick={(e) => {
                e.preventDefault();
                navigate('/services', { state: { selectedCategory: 'Plumbing Services' } });
              }}>Plumbing</a>
              <a href="/services" style={styles.footerLink} onClick={(e) => {
                e.preventDefault();
                navigate('/services', { state: { selectedCategory: 'Electrical Services' } });
              }}>Electrical</a>
              <a href="/services" style={styles.footerLink} onClick={(e) => {
                e.preventDefault();
                navigate('/services', { state: { selectedCategory: 'AC Services' } });
              }}>AC Service</a>
              <a href="/services" style={styles.footerLink} onClick={(e) => {
                e.preventDefault();
                navigate('/services', { state: { selectedCategory: 'Painting Services' } });
              }}>Painting</a>
            </div>
            
            <div style={styles.footerSection}>
              <h3 style={styles.footerTitle}>Company</h3>
              <a href="#about" style={styles.footerLink}>About Us</a>
              <a href="#careers" style={styles.footerLink}>Careers</a>
              <a href="#blog" style={styles.footerLink}>Blog</a>
              <a href="#contact" style={styles.footerLink}>Contact Us</a>
              <a href="#help" style={styles.footerLink}>Help Center</a>
            </div>
            
            <div style={styles.footerSection}>
              <h3 style={styles.footerTitle}>Contact</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Phone size={isMobile ? 14 : 16} color="#ccc" />
                <span style={{ color: '#ccc', fontSize: isMobile ? '13px' : '14px' }}>+91 12345 67890</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <MapPin size={isMobile ? 14 : 16} color="#ccc" />
                <span style={{ color: '#ccc', fontSize: isMobile ? '13px' : '14px' }}>123, Service Street, City</span>
              </div>
              <div style={{ color: '#ccc', fontSize: isMobile ? '13px' : '14px' }}>
                support@easemyhome.com
              </div>
            </div>
          </div>
          
          <div style={styles.footerBottom}>
            © 2024 EaseMyHome Services. All rights reserved.
          </div>
        </footer>
      )}

      {/* Mobile Menu */}
      <div style={{
        ...styles.mobileMenu,
        transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
      }}>
        <button 
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: textDark,
          }}
          onClick={() => setMobileMenuOpen(false)}
        >
          <X size={24} />
        </button>
        
        <div style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <a 
            href="#services" 
            style={{...styles.navLink, color: textDark}} 
            onClick={(e) => {
              e.preventDefault();
              navigate('/services');
              setMobileMenuOpen(false);
            }}
          >
            Services
          </a>
          <a 
            href="#popular-services" 
            style={{...styles.navLink, color: textDark}} 
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('popular-services');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
              setMobileMenuOpen(false);
            }}
          >
            Popular Services
          </a>
          <a 
            href="#how-it-works" 
            style={{...styles.navLink, color: textDark}} 
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('how-it-works');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
              setMobileMenuOpen(false);
            }}
          >
            How it Works
          </a>
          <a 
            href="#why-choose-us" 
            style={{...styles.navLink, color: textDark}} 
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('why-choose-us');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
              setMobileMenuOpen(false);
            }}
          >
            Why Choose Us
          </a>
          
          {!user && (
            <>
              <button 
                style={styles.loginBtn}
                onClick={() => {
                  setAuthType('login');
                  setShowAuthModal(true);
                  setMobileMenuOpen(false);
                }}
              >
                Login
              </button>
              <button 
                style={styles.signupBtn}
                onClick={() => {
                  setAuthType('signup');
                  setShowAuthModal(true);
                  setMobileMenuOpen(false);
                }}
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quick Add to Cart Modal */}
      {showQuickAddModal && quickAddService && (
        <div style={styles.modalOverlay} onClick={() => setShowQuickAddModal(false)}>
          <div style={styles.quickAddModal} onClick={(e) => e.stopPropagation()}>
            <button 
              style={styles.modalClose}
              onClick={() => setShowQuickAddModal(false)}
            >
              ×
            </button>
            
            <h3 style={{ marginBottom: '10px', color: textDark }}>Add to Cart</h3>
            <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>{quickAddService.name}</p>

            {/* Fixed Price Display */}
            {quickAddService.pricingType === 'fixed' && (
              <div style={styles.modalSection}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '15px',
                  backgroundColor: `${primaryColor}05`,
                  borderRadius: '8px'
                }}>
                  <span style={{ fontWeight: '600', color: textDark }}>Price:</span>
                  <span style={{ fontSize: '20px', fontWeight: '700', color: primaryColor }}>
                    ₹{quickAddService.calculatedPrice || quickAddService.basePrice || quickAddService.price}
                  </span>
                </div>
              </div>
            )}

            {/* BHK Selection */}
            {quickAddService.pricingType === 'bhk-based' && quickAddService.bhkPrices && (
              <div style={styles.modalSection}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: textDark }}>
                  Select Apartment Size:
                </label>
                <div style={styles.bhkOptions}>
                  {Object.entries(quickAddService.bhkPrices).map(([bhk, price]) => (
                    <button
                      key={bhk}
                      style={{
                        ...styles.bhkBtn,
                        border: quickAddBHK === bhk ? `2px solid ${primaryColor}` : '1px solid #ddd',
                        backgroundColor: quickAddBHK === bhk ? `${primaryColor}10` : 'white',
                      }}
                      onClick={() => setQuickAddBHK(bhk)}
                    >
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>{bhk}</span>
                      <span style={{ fontSize: '12px', color: primaryColor }}>₹{price}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Variant Selection */}
            {quickAddService.pricingType === 'variants' && quickAddService.variants && (
              <div style={styles.modalSection}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: textDark }}>
                  Select Option:
                </label>
                <div style={styles.variantOptions}>
                  {quickAddService.variants.map((variant) => (
                    <button
                      key={variant.id}
                      style={{
                        ...styles.variantBtn,
                        border: quickAddVariant?.id === variant.id ? `2px solid ${primaryColor}` : '1px solid #ddd',
                        backgroundColor: quickAddVariant?.id === variant.id ? `${primaryColor}10` : 'white',
                      }}
                      onClick={() => setQuickAddVariant(variant)}
                    >
                      <span style={{ fontWeight: '500' }}>{variant.name}</span>
                      <span style={{ fontWeight: '600', color: primaryColor }}>₹{variant.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selection */}
            <div style={styles.modalSection}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: textDark }}>
                Quantity:
              </label>
              <div style={styles.quantityControls}>
                <button 
                  style={styles.quantityBtn}
                  onClick={() => setQuickAddQuantity(Math.max(1, quickAddQuantity - 1))}
                  disabled={quickAddQuantity <= 1}
                >
                  -
                </button>
                <span style={{ fontSize: '18px', fontWeight: '600', minWidth: '30px', textAlign: 'center' }}>
                  {quickAddQuantity}
                </span>
                <button 
                  style={styles.quantityBtn}
                  onClick={() => setQuickAddQuantity(quickAddQuantity + 1)}
                >
                  +
                </button>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button 
                style={styles.btnCancel}
                onClick={() => setShowQuickAddModal(false)}
              >
                Cancel
              </button>
              <button 
                style={styles.btnAddToCart}
                onClick={confirmQuickAdd}
              >
                <ShoppingCart size={16} />
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        type={authType}
        onSwitchType={() => setAuthType(authType === 'login' ? 'signup' : 'login')}
        onLoginSuccess={onLoginSuccess}
      />

      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            animation: 'fadeIn 0.3s ease',
          }}
        ></div>
      )}

      {/* Add CSS animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes slideIn {
            from { transform: translateX(-100%); }
            to { transform: translateX(0); }
          }
          
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          
          .service-card {
            animation: fadeIn 0.5s ease-out;
          }
          
          .category-card {
            animation: slideIn 0.3s ease-out;
          }
          
          /* Popular badge animation */
          .popular-badge {
            animation: float 3s ease-in-out infinite;
          }
          
          /* Responsive styles */
          @media (max-width: 768px) {
            .hero {
              padding: 80px 0 30px !important;
            }
            
            .hero-content {
              text-align: center;
              margin: 0 auto 40px !important;
            }
            
            .hero-stats {
              justify-content: center !important;
              gap: 20px !important;
            }
            
            .stat-item {
              flex-direction: column !important;
              gap: 5px !important;
              text-align: center;
            }
            
            .stat-number {
              font-size: 20px !important;
            }
            
            .stat-label {
              font-size: 11px !important;
            }
            
            .categories-grid {
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 12px !important;
            }
            
            .category-card {
              padding: 12px 8px !important;
              min-height: 100px !important;
            }
            
            .category-name {
              font-size: 11px !important;
              line-height: 1.3;
            }
            
            .section-header {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 15px !important;
            }
            
            .section-title {
              font-size: 1.5rem !important;
            }
            
            .services-grid, .popular-services-grid {
              grid-template-columns: 1fr !important;
              gap: 20px !important;
            }
            
            .service-card {
              width: 100% !important;
              margin: 0 auto !important;
            }
            
            .step-icon {
              width: 70px !important;
              height: 70px !important;
            }
            
            .feature {
              padding: 20px !important;
            }
            
            .feature-title {
              font-size: 17px !important;
            }
            
            .feature-description {
              font-size: 14px !important;
            }
            
            .cta-button {
              width: 100% !important;
              max-width: 300px !important;
            }
            
            .footer-section {
              align-items: center !important;
              text-align: center !important;
            }
            
            .footer-title {
              text-align: center !important;
            }
            
            .quick-add-modal {
              padding: 20px !important;
            }
            
            .popular-services-header {
              margin-bottom: 25px !important;
            }
            
            .popular-services-title {
              font-size: 1.4rem !important;
            }
            
            .popular-services-subtitle {
              font-size: 13px !important;
              padding: 0 10px !important;
            }
          }
          
          @media (max-width: 480px) {
            .hero-title {
              font-size: 1.6rem !important;
            }
            
            .hero-subtitle {
              font-size: 0.9rem !important;
            }
            
            .categories-grid {
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 10px !important;
            }
            
            .category-card {
              padding: 10px 6px !important;
              min-height: 90px !important;
            }
            
            .category-name {
              font-size: 10px !important;
            }
            
            .section-title {
              font-size: 1.3rem !important;
            }
            
            .services-grid, .popular-services-grid {
              grid-template-columns: 1fr !important;
              gap: 15px !important;
            }
            
            .hero-search {
              flex-direction: column !important;
            }
            
            .search-button {
              width: 100% !important;
              justify-content: center !important;
            }
            
            .cta-button {
              width: 100% !important;
              justify-content: center !important;
            }
            
            .step-description {
              font-size: 12px !important;
            }
            
            .feature-description {
              font-size: 13px !important;
            }
            
            .bhk-options {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            
            .popular-services-title {
              font-size: 1.3rem !important;
            }
          }
          
          @media (max-width: 375px) {
            .categories-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            
            .category-card {
              min-height: 85px !important;
            }
            
            .hero-title {
              font-size: 1.4rem !important;
            }
            
            .hero-badge {
              font-size: 10px !important;
              padding: 6px 12px !important;
            }
            
            .popular-services-grid {
              grid-template-columns: 1fr !important;
              gap: 15px !important;
            }
          }
          
          @media (min-width: 769px) and (max-width: 1024px) {
            .services-grid {
              grid-templateColumns: repeat(2, 1fr) !important;
              gap: 25px !important;
            }
            
            .popular-services-grid {
              grid-templateColumns: repeat(2, 1fr) !important;
              gap: 25px !important;
            }
            
            .categories-grid {
              grid-template-columns: repeat(4, 1fr) !important;
            }
          }
          
          /* Desktop specific */
          @media (min-width: 769px) {
            .mobile-menu-btn {
              display: none !important;
            }
          }
          
          /* Better touch targets for mobile */
          @media (max-width: 768px) {
            button, 
            .category-card, 
            .service-card,
            .feature {
              min-height: 44px !important;
              touch-action: manipulation !important;
            }
            
            input, select {
              min-height: 48px !important;
              font-size: 16px !important;
            }
          }
          
          /* Prevent text size adjustment on iOS */
          html {
            -webkit-text-size-adjust: 100%;
            text-size-adjust: 100%;
          }
          
          /* Improve scroll performance */
          * {
            -webkit-overflow-scrolling: touch;
            -webkit-tap-highlight-color: transparent;
          }
          
          /* Smooth scrolling */
          html {
            scroll-behavior: smooth;
          }
          
          /* Improve hover effects */
          @media (hover: hover) and (pointer: fine) {
            .category-card:hover {
              transform: translateY(-5px) !important;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
            }
            
            .service-card:hover {
              transform: translateY(-5px) !important;
              box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1) !important;
            }
            
            .feature:hover {
              transform: translateY(-10px) !important;
              box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
            }
            
            .bhk-btn:hover:not(.active) {
              border-color: ${primaryColor}50 !important;
              background-color: ${primaryColor}05 !important;
            }
            
            .variant-btn:hover:not(.active) {
              border-color: ${primaryColor}50 !important;
              background-color: ${primaryColor}05 !important;
            }
          }
          
          /* Mobile tap effects */
          @media (max-width: 768px) {
            .category-card:active {
              transform: scale(0.98) !important;
              transition: transform 0.1s ease !important;
            }
            
            .service-card:active {
              transform: scale(0.99) !important;
              transition: transform 0.1s ease !important;
            }
            
            .bhk-btn:active {
              transform: scale(0.95) !important;
            }
            
            .variant-btn:active {
              transform: scale(0.98) !important;
            }
          }
          
          /* Loading state improvements */
          .loading-spinner {
            border-width: 4px;
          }
          
          /* Smooth animations for mobile menu */
          .mobile-menu {
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          
          /* Backdrop for mobile menu */
          .mobile-menu-backdrop {
            animation: fadeIn 0.3s ease;
          }
          
          /* Active state for modal buttons */
          .bhk-btn.active {
            border-color: ${primaryColor} !important;
            background-color: ${primaryColor}10 !important;
            color: ${primaryColor} !important;
          }
          
          .variant-btn.active {
            border-color: ${primaryColor} !important;
            background-color: ${primaryColor}10 !important;
          }
          
          .quantity-btn:active {
            background-color: #f0f0f0 !important;
          }
          
          .btn-add-to-cart:active {
            background-color: ${primaryDark} !important;
            transform: translateY(2px) !important;
          }
          
          /* Popular services badge animation */
          .popular-badge {
            animation: float 3s ease-in-out infinite;
          }
        `}
      </style>
    </div>
  );
};

export default HomePage;